import { motion } from 'framer-motion';
import { ArrowRight, Check, Headphones, Calendar, Zap } from 'lucide-react';
import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    restaurant: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Criar corpo do email
    const subject = `Nova solicitação de demo - ${formData.restaurant}`;
    const body = `Nome: ${formData.name}
E-mail: ${formData.email}
WhatsApp: ${formData.phone}
Restaurante: ${formData.restaurant}`;
    
    // Abrir cliente de email com os dados preenchidos
    window.location.href = `mailto:contato@avak.com.br?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const benefits = [
    'Vamos de turma na implementação',
    'Implementação gratuita',
    'Suporte por WhatsApp',
    'Sem fidelidade'
  ];

  return (
    <section id="contato" className="py-16 md:py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-medium mb-6">
              Comece Agora
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Pronto para 
              <span className="text-blue-600"> transformar</span> seu restaurante?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Agende uma demonstração gratuita e descubra como o AVAK pode 
              otimizar suas operações e aumentar seu faturamento.
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://wa.me/5511934664558" 
                target="_blank"
                className="flex items-center gap-3 bg-green-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-green-600 transition-colors"
              >
                <Headphones className="w-5 h-5" />
                Falar no WhatsApp
              </a>
              <a 
                href="https://wa.me/5511934664558?text=Olá! Gostaria de agendar uma demonstração do AVAK." 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-xl font-medium hover:border-blue-300 hover:text-blue-600 transition-colors"
              >
                <Calendar className="w-5 h-5" />
                Agendar Demo
              </a>
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Solicite uma Conversa</h3>
                  <p className="text-sm text-slate-500">Resposta em até 3 horas</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Seu nome
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="João Silva"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="joao@restaurante.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="(11) 9999-9999"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nome do restaurante
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Restaurante XYZ"
                    value={formData.restaurant}
                    onChange={(e) => setFormData({ ...formData, restaurant: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25"
                >
                  Enviar Mensagem
                  <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-center text-sm text-slate-500">
                  Ao enviar, você concorda com nossa{' '}
                  <a href="#contato" className="text-blue-600 hover:underline">Política de Privacidade</a>
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
